/**
 * TasksTable Component
 *
 * Full-featured tasks table with:
 * - Header with "Create Tasks from Description" and "Add New Task" buttons
 * - Sortable columns (Name, Description, Funnel Goals, Required Info)
 * - Selection checkboxes with select-all
 * - Actions menu (Edit, Delete, Duplicate, Generate Audience)
 * - Task form dialog for create/edit
 * - Delete confirmation dialog
 *
 * Built on `SDataTable` (the project's canonical table primitive, wrapping
 * Material React Table v3). Public `TasksTableProps` is unchanged from the
 * previous raw-MUI implementation; callers are untouched.
 *
 * @module product
 * @sprint 6
 * @requirements FR-014 (footer conditional rendering based on tasks.length)
 * @requirements FR-017 (selectedTaskIds drives Create Audiences button state)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
// Subpath import is intentional: the `@modules/ui-components` barrel
// transitively re-exports `DataReferencePickerDialog` → data-pickers →
// firebase-core/CoreFirebaseService, which uses `import.meta.env` and breaks
// jest (documented as a known issue in ui-components/MODULE.md). Importing
// SDataTable directly from its folder avoids pulling in that Firebase chain
// into the test environment. When the barrel is cleaned up, this can move
// back to `@modules/ui-components`.
import {
  SDataTable,
  useSelectionMode,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import type { Task } from '@segmently/schema-models/entities';
import { VariableSetEditor } from './VariableSetEditor';

// ============================================================================
// TYPESCRIPT INTERFACES
// ============================================================================

/**
 * Funnel Goal structure
 */
interface FunnelGoal {
  goal: string;
  priority: number;
}

/**
 * Form data for task create/edit
 */
interface TaskFormData {
  name: string;
  description: string;
  requiredInfo: string[];
  funnelGoals: FunnelGoal[];
  requiredTimeMinutes: number;
  priority: number;
  userTrustLevel: number;
  competitorsForClients: string[];
  userCondition: string[];
  activationAction: string;
}

/**
 * Props for TasksTable component
 */
export interface TasksTableProps {
  /** Array of tasks to display */
  tasks: Task[];
  /** Callback when task selection changes - propagates selected task IDs to parent */
  onSelectionChange: (selectedTaskIds: string[]) => void;
  /** Callback for Create Tasks from Description button */
  onCreateTasksFromDescription?: () => Promise<void>;
  /** Callback for creating a new task */
  onCreateTask?: (taskData: Partial<Task>) => Promise<void>;
  /** Callback for updating an existing task */
  onUpdateTask?: (taskId: string, taskData: Partial<Task>) => Promise<void>;
  /** Callback for deleting a task */
  onDeleteTask?: (taskId: string) => Promise<void>;
  /** Callback for duplicating a task */
  onDuplicateTask?: (task: Task) => Promise<void>;
  /** Callback for generating audience for a task */
  onGenerateAudience?: (task: Task) => Promise<void>;
  /** Callback for deleting selected tasks (shown in selection toolbar) */
  onDeleteSelectedTasks?: (selectedTaskIds: string[]) => Promise<void>;
  /** Whether any operation is running (disables interactions) */
  isOperationRunning?: boolean;
  /** Whether description is saved (enables Create Tasks button) */
  isDescriptionSaved?: boolean;
  /** Project-level variables for task-scoped variable display */
  variables?: Record<string, import('./VariableFormDialog').VariableFormData>;
  /** Callback when variables change (for adding variables from task form) */
  onVariablesChange?: import('@modules/product/components/VariableSetEditor').VariableChangeHandler;
}

// ============================================================================
// DEFAULT FORM DATA
// ============================================================================

const defaultFormData: TaskFormData = {
  name: '',
  description: '',
  requiredInfo: [],
  funnelGoals: [],
  requiredTimeMinutes: 30,
  priority: 1,
  userTrustLevel: 5,
  competitorsForClients: [],
  userCondition: [],
  activationAction: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

export const TasksTable: React.FC<TasksTableProps> = ({
  tasks,
  onSelectionChange,
  onCreateTasksFromDescription,
  onCreateTask,
  onUpdateTask,
  onDeleteTask,
  onDuplicateTask,
  onGenerateAudience,
  onDeleteSelectedTasks,
  isOperationRunning = false,
  isDescriptionSaved = true,
  variables: projectVariables = {},
  onVariablesChange,
}) => {
  const theme = useTheme();

  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Selection state + mode — managed via the shared `useSelectionMode` hook
   * (canonical adoption, see `13-DataTables.mdx`). TasksTable is an
   * "always-on" consumer (bulk UX is its primary purpose), so we pass
   * `defaultMode: true`. `onSelectionChange` propagates to the parent and
   * matches the pre-existing `string[]` public contract byte-for-byte.
   */
  const selection = useSelectionMode({
    defaultMode: true,
    onSelectionChange,
  });
  const { rowSelection, setRowSelection, selectedIds } = selection;

  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState<TaskFormData>(defaultFormData);
  const [tempRequiredInfo, setTempRequiredInfo] = useState('');

  // Delete confirmation dialog
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  // Loading states
  const [isCreatingTasks, setIsCreatingTasks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  // ============================================================================
  // SELECTION ADAPTERS
  // ============================================================================

  /**
   * When tasks list changes externally (e.g. bulk delete, AI regen), drop
   * selection keys no longer present in the data. `useSelectionMode` does
   * not watch the upstream data for us — it's data-agnostic by design —
   * so this one-liner lives here.
   */
  useEffect(() => {
    const validIds = new Set(tasks.map((t) => t.id).filter(Boolean) as string[]);
    setRowSelection((prev) => {
      const keys = Object.keys(prev);
      const cleaned: Record<string, boolean> = {};
      let changed = false;
      for (const key of keys) {
        if (validIds.has(key)) {
          cleaned[key] = prev[key];
        } else {
          changed = true;
        }
      }
      return changed ? cleaned : prev;
    });
  }, [tasks, setRowSelection]);

  // ============================================================================
  // DIALOG / FORM HANDLERS (unchanged from previous implementation)
  // ============================================================================

  const handleOpenForm = useCallback((task: Task | null = null) => {
    if (task) {
      setCurrentTask(task);
      setFormData({
        name: task.name || '',
        description: task.description || '',
        requiredInfo: task.requiredInfo || [],
        funnelGoals: (task.funnelGoals as FunnelGoal[]) || [],
        requiredTimeMinutes: task.requiredTimeMinutes || 30,
        priority: task.priority || 1,
        userTrustLevel: task.userTrustLevel || 5,
        competitorsForClients: task.competitorsForClients || [],
        userCondition: task.userCondition || [],
        activationAction: (task as any).activationAction || '',
      });
    } else {
      setCurrentTask(null);
      setFormData(defaultFormData);
    }
    setTempRequiredInfo('');
    setOpenForm(true);
  }, []);

  const handleCloseForm = useCallback(() => {
    setOpenForm(false);
    setCurrentTask(null);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleAddRequiredInfo = useCallback(() => {
    if (!tempRequiredInfo.trim()) return;
    setFormData((prev) => ({
      ...prev,
      requiredInfo: [...prev.requiredInfo, tempRequiredInfo.trim()],
    }));
    setTempRequiredInfo('');
  }, [tempRequiredInfo]);

  const handleRemoveRequiredInfo = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      requiredInfo: prev.requiredInfo.filter((_, i) => i !== index),
    }));
  }, []);

  const handleAddFunnelGoal = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      funnelGoals: [
        ...prev.funnelGoals,
        { goal: '', priority: prev.funnelGoals.length + 1 },
      ],
    }));
  }, []);

  const handleDeleteFunnelGoal = useCallback((index: number) => {
    setFormData((prev) => {
      const newGoals = [...prev.funnelGoals];
      newGoals.splice(index, 1);
      const updatedGoals = newGoals.map((goal, idx) => ({
        ...goal,
        priority: idx + 1,
      }));
      return { ...prev, funnelGoals: updatedGoals };
    });
  }, []);

  const handleFunnelGoalChange = useCallback(
    (index: number, field: 'goal' | 'priority', value: string) => {
      setFormData((prev) => {
        const newGoals = [...prev.funnelGoals];
        if (field === 'priority') {
          newGoals[index] = { ...newGoals[index], priority: Number(value) || 1 };
        } else {
          newGoals[index] = { ...newGoals[index], goal: value };
        }
        return { ...prev, funnelGoals: newGoals };
      });
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      // `activationAction` is not yet in the generated `Task` schema but is
      // still accepted by the backend and rendered in the form. Cast covers
      // the schema-vs-UI gap; runtime behaviour unchanged from pre-migration.
      const taskData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        requiredInfo: formData.requiredInfo,
        funnelGoals: formData.funnelGoals.filter((g) => g.goal.trim() !== ''),
        requiredTimeMinutes: Number(formData.requiredTimeMinutes),
        priority: Number(formData.priority),
        userTrustLevel: Number(formData.userTrustLevel),
        competitorsForClients: formData.competitorsForClients,
        userCondition: formData.userCondition,
        activationAction: formData.activationAction?.trim() || '',
      } as Partial<Task>;

      if (currentTask && currentTask.id && onUpdateTask) {
        await onUpdateTask(currentTask.id, taskData);
      } else if (onCreateTask) {
        await onCreateTask(taskData);
      }

      handleCloseForm();
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, currentTask, onUpdateTask, onCreateTask, handleCloseForm]);

  // ============================================================================
  // DELETE HANDLERS
  // ============================================================================

  const handleDeleteTask = useCallback((taskId: string) => {
    setTaskToDelete(taskId);
    setOpenDeleteConfirm(true);
  }, []);

  const confirmDeleteTask = useCallback(async () => {
    if (!taskToDelete || !onDeleteTask) return;

    setIsSubmitting(true);
    try {
      await onDeleteTask(taskToDelete);
      setOpenDeleteConfirm(false);
      setTaskToDelete(null);
    } finally {
      setIsSubmitting(false);
    }
  }, [taskToDelete, onDeleteTask]);

  // ============================================================================
  // ACTION HANDLERS
  // ============================================================================

  const handleCreateTasksFromDescription = useCallback(async () => {
    if (!onCreateTasksFromDescription) return;
    setIsCreatingTasks(true);
    try {
      await onCreateTasksFromDescription();
    } finally {
      setIsCreatingTasks(false);
    }
  }, [onCreateTasksFromDescription]);

  const handleDuplicate = useCallback(
    async (task: Task) => {
      if (onDuplicateTask) {
        await onDuplicateTask(task);
      }
    },
    [onDuplicateTask],
  );

  const handleGenerateAudience = useCallback(
    async (task: Task) => {
      if (onGenerateAudience) {
        await onGenerateAudience(task);
      }
    },
    [onGenerateAudience],
  );

  // ============================================================================
  // SELECTION TOOLBAR HANDLER
  // ============================================================================

  const handleDeleteSelected = useCallback(async () => {
    if (!onDeleteSelectedTasks || selectedIds.length === 0) return;

    setIsDeletingSelected(true);
    try {
      await onDeleteSelectedTasks(selectedIds);
      // Clearing rowSelection triggers useSelectionMode's onSelectionChange
      // emission — matches the pre-migration contract byte-for-byte.
      selection.clearSelection();
    } finally {
      setIsDeletingSelected(false);
    }
  }, [onDeleteSelectedTasks, selectedIds, selection]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  /**
   * MRT columns. Sorting for chip columns is based on array LENGTH (same
   * behaviour as the pre-migration `sortedTasks` memo cases for
   * `funnelGoals` / `requiredInfo`).
   */
  const columns = useMemo<SDataTableColumnDef<Task>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => cell.getValue<string>() || 'Unnamed Task',
      },
      {
        accessorKey: 'description',
        header: 'Description',
        muiTableBodyCellProps: {
          sx: {
            maxWidth: 300,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            borderBottom: '1px solid',
            borderColor: 'divider',
          },
        },
        Cell: ({ cell }) => cell.getValue<string>() || '-',
      },
      {
        accessorKey: 'funnelGoals',
        header: 'Funnel Goals',
        sortingFn: (rowA, rowB) => {
          const a = ((rowA.original.funnelGoals as FunnelGoal[]) || []).length;
          const b = ((rowB.original.funnelGoals as FunnelGoal[]) || []).length;
          return a - b;
        },
        Cell: ({ cell }) => {
          const goals = (cell.getValue<FunnelGoal[]>() || []);
          if (goals.length === 0) return 'None';
          return (
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 150 }}
            >
              {goals.map((goal, idx) => (
                <Chip
                  key={idx}
                  label={
                    goal.goal
                      ? `${goal.goal.substring(0, 20)}...`
                      : `Goal ${goal.priority}`
                  }
                  size="small"
                />
              ))}
            </Box>
          );
        },
      },
      {
        accessorKey: 'requiredInfo',
        header: 'Required Info',
        sortingFn: (rowA, rowB) => {
          const a = (rowA.original.requiredInfo || []).length;
          const b = (rowB.original.requiredInfo || []).length;
          return a - b;
        },
        Cell: ({ cell }) => {
          const info = cell.getValue<string[]>() || [];
          if (info.length === 0) return 'None';
          return (
            <Box
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 150 }}
            >
              {info.map((item, idx) => (
                <Chip key={idx} label={item} size="small" />
              ))}
            </Box>
          );
        },
      },
    ],
    [],
  );

  // ============================================================================
  // RENDER: HEADER
  // ============================================================================

  const renderHeader = () => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        mb: 2,
        alignItems: 'center',
      }}
    >
      {/* LEFT SIDE: Title + Selection Badge + Delete Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Tasks
        </Typography>

        {/* Selection badge - only when items selected */}
        {selectedIds.length > 0 && (
          <>
            <Chip
              label={selectedIds.length}
              size="small"
              color="primary"
              sx={{ fontWeight: 'medium' }}
            />
            <Tooltip title="Delete selected">
              <IconButton
                onClick={handleDeleteSelected}
                disabled={isDeletingSelected || isOperationRunning}
                color="error"
                size="small"
                aria-label="Delete"
              >
                {isDeletingSelected ? (
                  <CircularProgress size={18} color="error" />
                ) : (
                  <DeleteIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* RIGHT SIDE: Actions (always visible) */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={
            isCreatingTasks ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AssignmentIcon />
            )
          }
          onClick={handleCreateTasksFromDescription}
          disabled={isCreatingTasks || !isDescriptionSaved || isOperationRunning}
        >
          {isCreatingTasks ? 'Creating...' : 'Create Tasks from Description'}
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenForm()}
          disabled={isOperationRunning}
        >
          Add New Task
        </Button>
      </Box>
    </Box>
  );

  // ============================================================================
  // RENDER: FORM DIALOG
  // ============================================================================

  function renderFormDialog() {
    return (
      <Dialog
        open={openForm}
        onClose={handleCloseForm}
        maxWidth="md"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        {/* iOS nav bar layout: close LEFT, title CENTER, phantom RIGHT.
            Matches the Phase-4 SDialog standard; the Create/Edit dialog
            uses raw MUI Dialog (not SDialog) so we apply the grid
            manually here. */}
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            pb: 1,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCloseForm}
              disabled={isSubmitting}
              size="small"
              aria-label="close dialog"
              sx={{
                backgroundColor: (theme) => theme.palette.action.hover,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {currentTask ? 'Edit Task' : 'Create New Task'}
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Basic Info */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Task Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <TextField
                fullWidth
                label="Priority"
                name="priority"
                type="number"
                value={formData.priority}
                onChange={handleInputChange}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ maxWidth: 150 }}
              />
            </Box>

            {/* Description */}
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
            />

            {/* Funnel Goals */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Funnel Goals
              </Typography>
              {formData.funnelGoals.map((goalItem, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    fullWidth
                    label={`Goal ${index + 1}`}
                    size="small"
                    value={goalItem.goal}
                    onChange={(e) =>
                      handleFunnelGoalChange(index, 'goal', e.target.value)
                    }
                  />
                  <TextField
                    label="Priority"
                    type="number"
                    size="small"
                    value={goalItem.priority}
                    onChange={(e) =>
                      handleFunnelGoalChange(index, 'priority', e.target.value)
                    }
                    InputProps={{ inputProps: { min: 1 } }}
                    sx={{ width: 100 }}
                  />
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteFunnelGoal(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button
                startIcon={<AddIcon fontSize="small" />}
                onClick={handleAddFunnelGoal}
                size="small"
              >
                Add Goal
              </Button>
            </Box>

            {/* Activation Action */}
            <TextField
              label="Activation Action (Aha Moment)"
              placeholder="e.g., Log first meal and see calorie breakdown"
              value={formData.activationAction}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  activationAction: e.target.value,
                }))
              }
              fullWidth
              size="small"
              helperText="The key action user must complete to experience value for this task"
            />

            {/* Task Variables */}
            {currentTask && onVariablesChange && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Variables
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1 }}
                >
                  Variables scoped to this task. Adding a variable here
                  auto-assigns it to this task.
                </Typography>
                <VariableSetEditor
                  variables={projectVariables}
                  onChange={onVariablesChange}
                  tasks={tasks.map((t) => ({ id: t.id!, name: t.name ?? t.id! }))}
                  filterTaskId={currentTask.id}
                  disabled={isSubmitting}
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ pt: 2 }}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting || !formData.name.trim()}
          >
            {isSubmitting ? (
              <CircularProgress size={18} />
            ) : currentTask ? (
              'Update Task'
            ) : (
              'Create Task'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ============================================================================
  // RENDER: DELETE DIALOG
  // ============================================================================

  function renderDeleteDialog() {
    return (
      <Dialog
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
      >
        {/* Destructive confirmation — no header X. Footer Cancel/Delete
            is the only decision surface. Matches the Phase-4 rule for
            destructive dialogs. Title centred to mirror the SDialog
            standard visually. */}
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box />
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Confirm Delete
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this task? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDeleteConfirm(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteTask}
            color="error"
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ============================================================================
  // RENDER: EMPTY STATE (short-circuit, matches pre-migration behaviour)
  // ============================================================================

  if (tasks.length === 0) {
    return (
      <Box>
        {renderHeader()}
        <Box
          sx={{
            padding: theme.spacing(4),
            textAlign: 'center',
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">
            No tasks found. Click "Add New Task" to create one.
          </Typography>
        </Box>

        {renderFormDialog()}
      </Box>
    );
  }

  // ============================================================================
  // RENDER: TABLE
  // ============================================================================

  return (
    <Box>
      {renderHeader()}

      <SDataTable<Task>
        columns={columns}
        data={tasks}
        getRowId={(row) => row.id || ''}
        enableRowSelection
        enableRowActions
        enableSorting
        // Suppress MRT's internal top toolbar — selection UX lives in the
        // section header (renderHeader above), preserving the pre-migration
        // UX exactly.
        enableTopToolbar={false}
        state={{ rowSelection }}
        onRowSelectionChange={setRowSelection}
        muiSelectCheckboxProps={{
          size: 'small',
          color: 'primary',
          disabled: isOperationRunning,
        }}
        muiSelectAllCheckboxProps={{
          size: 'small',
          disabled: isOperationRunning || tasks.length === 0,
        }}
        muiTableBodyRowProps={({ row }) => ({
          hover: true,
          onClick: () => handleOpenForm(row.original),
          sx: { cursor: 'pointer' },
        })}
        renderRowActionMenuItems={({ row, closeMenu }) => [
          <MenuItem
            key="edit"
            onClick={() => {
              closeMenu();
              handleOpenForm(row.original);
            }}
            disabled={isOperationRunning}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              closeMenu();
              handleDeleteTask(row.original.id || '');
            }}
            disabled={isOperationRunning}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>,
          <MenuItem
            key="duplicate"
            onClick={() => {
              closeMenu();
              handleDuplicate(row.original);
            }}
            disabled={isOperationRunning}
          >
            Duplicate
          </MenuItem>,
          <MenuItem
            key="generate"
            onClick={() => {
              closeMenu();
              handleGenerateAudience(row.original);
            }}
            disabled={isOperationRunning}
          >
            Generate Audience
          </MenuItem>,
        ]}
      />

      {renderFormDialog()}
      {renderDeleteDialog()}
    </Box>
  );
};
